import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "@/hooks/useToast";
import { eventsApi } from "@/lib/humhub-api/events";
import type { RecurrenceScope, ReminderData } from "@/lib/humhub-api/types";
import { queryInvalidationHelpers } from "@/lib/queryInvalidationHelpers";

interface UseUpdateAllParticipantsRemindersOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export const useUpdateAllParticipantsReminders = (
  options?: UseUpdateAllParticipantsRemindersOptions,
) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const mutation = useMutation({
    mutationFn: async ({
      eventId,
      reminders,
      scope,
    }: {
      eventId: number;
      reminders: ReminderData[];
      scope?: RecurrenceScope;
    }) => eventsApi.updateAllParticipantsReminders(eventId, reminders, scope),
    onSuccess: () => {
      queryInvalidationHelpers.invalidateEventQueries(queryClient);
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      console.error("Failed to update reminders for all participants:", error);
      toast({
        title: t("common.error"),
        description: t("eventManage.reminderUpdateFailed"),
        variant: "error",
      });
      options?.onError?.(error);
    },
  });

  const updateAllParticipantsReminders = useCallback(
    async (
      eventId: number,
      reminders: ReminderData[],
      scope?: RecurrenceScope,
    ) => {
      return mutation.mutateAsync({ eventId, reminders, scope });
    },
    [mutation],
  );

  return {
    updateAllParticipantsReminders,
    isLoading: mutation.isPending,
    error: mutation.error,
  };
};
